import React, { useEffect, useRef } from 'react';
import { loadFFHeaderScript } from '~/app/utils/loadFFWidgetScript';

/**
 * Mounts the CDN-delivered FFHeader into a local container.
 *
 * @param {object} props
 * @param {string} props.region - 'us' | 'cn'
 * @param {object} [props.props] - Menu props passed through to FFHeader.mount
 * @param {string} [props.className]
 * @param {string} props.scriptUrl - Absolute URL of ff-header.js
 */
export default function RemoteFFHeader({ region, props: headerProps, className, scriptUrl }) {
  const containerRef = useRef(null);
  // Content fingerprint so parent re-renders with identical props do not remount
  const propsKey = JSON.stringify(headerProps ?? null);

  useEffect(() => {
    let cancelled = false;
    const el = containerRef.current;

    if (!el || !scriptUrl) {
      return undefined;
    }

    const mountProps = headerProps;

    loadFFHeaderScript(scriptUrl)
      .then((FFHeader) => {
        if (cancelled || !FFHeader?.mount) {
          return;
        }
        FFHeader.mount(el, { region, props: mountProps, className });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[RemoteFFHeader] failed to load/mount', err);
      });

    return () => {
      cancelled = true;
      if (el && window.FFHeader?.unmount) {
        try {
          window.FFHeader.unmount(el);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[RemoteFFHeader] unmount failed', err);
        }
      }
    };
    // propsKey tracks headerProps content; do not depend on object identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, className, scriptUrl, propsKey]);

  return <div ref={containerRef} data-ff-header />;
}
